import React, { useEffect, useRef, useState } from 'react'
import { auth, firestore, realtime } from '../firebase-config'
import { get, onValue, push, ref, remove, set, update } from 'firebase/database'
import { dark_mode_primary, dark_mode_secondary, Priority, TaskStatus, AppMode } from './values'
import { collection, addDoc, doc, updateDoc, deleteDoc, query, where, onSnapshot, getDoc, getDocs } from 'firebase/firestore'
import { AddMemberModal, AssignmentDetails, CreateTeamModal, DoneTasks, InProgress, ProfileHeader, TaskImageUpload, ToDo } from './widgets'

export function Tasks({ setAuthStatus, appMode, toggleAppMode }) {
    const dropdownRef = useRef(null);
    const [teams, setTeams] = useState([]);
    const [teamMembers, setTeamMembers] = useState([]);
    const [currentTeam, setCurrentTeam] = useState(null);
    const [selectedTask, setSelectedTask] = useState(null);
    const [showTeamModal, setShowTeamModal] = useState(false);
    const [showAddMemberModal, setShowAddMemberModal] = useState(false);
    const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);

    useEffect(() => {
        const fetchTeams = async () => {
            try {
                if (!auth.currentUser) return;
                const userTeamsRef = ref(realtime, 'teams');
                const unsubscribe = onValue(userTeamsRef, (snapshot) => {
                    const teamsData = snapshot.val() || {};
                    const teamsList = Object.entries(teamsData).map(([id, data]) => {
                        return {
                            id,
                            ...data
                        };
                    }).filter(team => {
                        const isTeamMember = team.members && team.members.includes(auth.currentUser.uid);
                        return isTeamMember;
                    });
                    setTeams(teamsList);
                    if (teamsList.length > 0 && !currentTeam) {
                        setCurrentTeam(teamsList[0]);
                        fetchTeamMembers(teamsList[0].id);
                    }
                });
                return unsubscribe;
            } catch (error) {
            }
        };
        fetchTeams();
    }, [currentTeam]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowTeamModal(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [setShowTeamModal]);

    const fetchTeamMembers = async (teamId) => {
        try {
            const teamRef = ref(realtime, `teams/${teamId}`);
            const unsubscribe = onValue(teamRef, (snapshot) => {
                const teamData = snapshot.val();
                if (teamData) {
                    const memberIds = (teamData.members || []).filter(id => !!id);
                    const fetchMemberDetails = async () => {
                        const membersData = await Promise.all(
                            memberIds.map(async (memberId) => {
                                try {
                                    const userQuery = query(
                                        collection(firestore, 'users'),
                                        where('userId', '==', memberId)
                                    );
                                    const userSnapshot = await getDocs(userQuery);
                                    if (!userSnapshot.empty) {
                                        const userData = userSnapshot.docs[0].data();
                                        return {
                                            id: memberId,
                                            ...userData,
                                            role: teamData.roles?.[memberId] || 'member'
                                        };
                                    }
                                    return null;
                                } catch (error) {
                                    return null;
                                }
                            })
                        );
                        setTeamMembers(membersData.filter(Boolean));
                    };
                    fetchMemberDetails();
                }
            });
            return unsubscribe;
        }
        catch (error) { }
    };

    const handleTeamChange = (team) => {
        setCurrentTeam(team);
        fetchTeamMembers(team.id);
    };

    const handlePersonalTasks = () => {
        const personalTeam = {
            isPersonal: true,
            name: "Personal Tasks",
            members: [auth.currentUser.uid],
            id: `personal_${auth.currentUser.uid}`,
            roles: { [auth.currentUser.uid]: 'admin' },
        };

        setCurrentTeam(personalTeam);
        setTeamMembers([{
            role: 'admin',
            id: auth.currentUser.uid,
            email: auth.currentUser.email,
            photoURL: auth.currentUser.photoURL,
            displayName: auth.currentUser.displayName || auth.currentUser.email,
        }]);
    };

    const handleAllTasks = () => { }

    return (
        <div className={`flex h-screen ${appMode === AppMode.LIGHT ? 'bg-gray-50' : dark_mode_secondary}`}>
            <div className="px-2.5 py-1.5 w-2/3 rounded">
                <div className={`h-full overflow-hidden ${appMode === AppMode.LIGHT ? 'bg-white shadow-lg' : dark_mode_secondary} rounded-xl flex-col overview border ${appMode === AppMode.LIGHT ? 'border-gray-100' : 'border-gray-700'}`}>
                    <ProfileHeader appMode={appMode} subtitle={true} setAuthStatus={setAuthStatus} toggleAppMode={toggleAppMode} />
                    <div className={`px-6 pt-5 pb-7 h-16 w-full flex justify-between items-center border-b ${appMode === AppMode.LIGHT ? 'border-gray-100' : 'border-gray-900'}`}>
                        <div className="relative" ref={dropdownRef}>
                            <button className={`p-3 ${appMode === AppMode.LIGHT ? 'bg-white border border-gray-200 text-gray-800 hover:bg-gray-50' : dark_mode_primary} font-medium rounded-lg flex items-center shadow-sm transition-colors gap-2`} onClick={() => setShowTeamModal(!showTeamModal)}>
                                <span className="flex items-center">
                                    {currentTeam?.isPersonal
                                        ? <i className="bi bi-person-circle mr-2 text-blue-500"></i>
                                        : <i className="bi bi-people-fill mr-2 text-blue-500"></i>
                                    }
                                    <span className="truncate max-w-[150px]">{currentTeam ? currentTeam.name : "Select Team"}</span>
                                </span>
                                <i className="bi bi-chevron-down text-sm text-gray-400"></i>
                            </button>
                            {showTeamModal && (
                                <div className={`absolute top-full left-0 mt-2 w-64 rounded-lg shadow-xl ${appMode === AppMode.LIGHT ? 'bg-white border border-gray-100' : dark_mode_primary + ' border border-gray-700 shadow-gray-900'} z-10 overflow-hidden`}>
                                    <div className="py-1">
                                        <button className={`block w-full text-left px-4 py-3 text-sm font-medium ${appMode === AppMode.LIGHT ? 'text-blue-600 hover:bg-blue-50' : 'text-blue-400 hover:bg-gray-800'} flex items-center transition-colors`} onClick={() => { setShowTeamModal(false); handleAllTasks(); }}>
                                            <i className="bi bi-collection mr-2.5 text-blue-500"></i> All Tasks
                                        </button>
                                        <button className={`block w-full text-left px-4 py-3 text-sm font-medium ${appMode === AppMode.LIGHT ? 'text-blue-600 hover:bg-blue-50' : 'text-blue-400 hover:bg-gray-800'} flex items-center transition-colors`} onClick={() => { setShowTeamModal(false); handlePersonalTasks(); }}>
                                            <i className="bi bi-person-circle mr-2.5 text-blue-500"></i> Personal Tasks
                                        </button>
                                        <div className={`px-4 flex w-full justify-between items-center py-3 border-b ${appMode === AppMode.LIGHT ? 'border-gray-100' : 'border-gray-700'}`}>
                                            <h1 className={`block w-full text-left text-sm ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-400'} font-medium`}>Teams</h1>
                                            <div className={`px-2.5 py-1 rounded-full text-center text-xs font-medium flex items-center justify-center ${appMode === AppMode.LIGHT ? 'bg-blue-50 text-blue-600' : 'bg-blue-900/30 text-blue-300'}`}>{teams.length}</div>
                                        </div>
                                        <div className="max-h-48 overflow-y-auto">
                                            {teams.map(team => (
                                                <button key={team.id} className={`block w-full text-left px-4 py-3 text-sm ${appMode === AppMode.LIGHT ? 'hover:bg-gray-50 text-gray-700' : 'hover:bg-gray-800 text-gray-200'} ${currentTeam?.id === team.id ? (appMode === AppMode.LIGHT ? 'bg-blue-50 text-blue-600 font-medium' : 'bg-gray-800 text-white font-medium') : ''} transition-colors`} onClick={() => { handleTeamChange(team); setShowTeamModal(false); }}>
                                                    <div className="flex items-center">
                                                        <i className="bi bi-people-fill mr-2.5 text-blue-500"></i>
                                                        <span className="truncate">{team.name}</span>
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                        <button className={`block w-full text-left px-4 py-3 text-sm font-medium ${appMode === AppMode.LIGHT ? 'text-blue-600 hover:bg-blue-50' : 'text-blue-400 hover:bg-gray-800'} border-t ${appMode === AppMode.LIGHT ? 'border-gray-100' : 'border-gray-700'} transition-colors`} onClick={() => { setShowTeamModal(false); setShowCreateTeamModal(true); }}>
                                            <div className="flex items-center">
                                                <i className="bi bi-plus-circle mr-2.5 text-blue-500"></i> Create New Team
                                            </div>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                        {!currentTeam?.isPersonal && (
                            <div className="flex items-center">
                                <div className="flex items-center mr-2">
                                    {teamMembers.slice(0, 3).map((member, index) => (
                                        <div key={member.id} className={`w-9 h-9 rounded-full overflow-hidden border-2 ${appMode === AppMode.LIGHT ? 'border-white' : 'border-gray-700'} shadow-sm`} style={{ marginLeft: index > 0 ? '-8px' : '0' }} title={member.displayName || member.email}>
                                            {member.photoURL
                                                ? <img src={member.photoURL} alt='' className="w-full h-full object-cover" />
                                                : <div className={`w-full h-full flex items-center justify-center ${appMode === AppMode.LIGHT ? 'bg-blue-100 text-blue-600' : 'bg-blue-900 text-blue-200'} font-medium`}>{(member.displayName || member.email || 'U').charAt(0).toUpperCase()}</div>
                                            }
                                        </div>
                                    ))}
                                    {teamMembers.length > 3 && (
                                        <div className={`w-9 h-9 rounded-full overflow-hidden border-2 border-white flex items-center justify-center ${appMode === AppMode.LIGHT ? 'bg-gray-100 text-gray-600' : 'bg-gray-700 text-gray-200'} shadow-sm font-medium`} style={{ marginLeft: '-8px' }} title={`${teamMembers.length - 3} more members`}>
                                            <span className="text-xs">+{teamMembers.length - 3}</span>
                                        </div>
                                    )}
                                </div>
                                <button className={`w-9 h-9 rounded-lg flex items-center justify-center ${appMode === AppMode.LIGHT ? 'bg-blue-50 text-blue-600 border border-blue-100 hover:bg-blue-100 shadow-sm' : 'bg-blue-900/20 text-blue-400 border border-blue-900/30 hover:bg-blue-900/30'} ml-2 transition-colors`} onClick={() => currentTeam && setShowAddMemberModal(true)} disabled={!currentTeam} title="Add team member">
                                    <i className="bi bi-plus"></i>
                                </button>
                            </div>
                        )}
                    </div>
                    {showCreateTeamModal && <CreateTeamModal appMode={appMode} onClose={() => setShowCreateTeamModal(false)} onTeamCreated={(team) => { setCurrentTeam(team); fetchTeamMembers(team.id); }} />}
                    {showAddMemberModal && <AddMemberModal appMode={appMode} onClose={() => setShowAddMemberModal(false)} teamId={currentTeam?.id} onMemberAdded={(newMember) => { setTeamMembers(prev => [...prev, newMember]); }} />}
                    <div className="w-full flex h-[calc(100%-80px)] items-stretch overflow-y-auto overflow-x-hidden p-3 gap-4">
                        <ToDo appMode={appMode} teamId={currentTeam?.id} setSelectedTask={setSelectedTask} />
                        <InProgress appMode={appMode} teamId={currentTeam?.id} setSelectedTask={setSelectedTask} />
                        <DoneTasks appMode={appMode} teamId={currentTeam?.id} setSelectedTask={setSelectedTask} />
                    </div>
                </div>
            </div>
            <div className={`p-4 w-1/3 h-screen overflow-y-auto ${appMode === AppMode.LIGHT ? 'bg-white border-l border-gray-100' : dark_mode_primary + ' border-l border-gray-700'}`}>
                {selectedTask
                    ? <>
                        <div className="px-3.5 py-5 w-full flex justify-between items-center">
                            <button className='font-semibold'>Task Info</button>
                            <button className="bi bi-x-lg" onClick={() => { setSelectedTask(null); }}></button>
                        </div>
                        <h1 className='px-3.5 w-4/5 font-semibold text-2xl capitalize'>{selectedTask.title}</h1>
                        <div className="px-3.5 py-4 flex justify-between items-center">
                            <h1 className={appMode === AppMode.LIGHT ? 'text-[#141522]' : 'text-white'}>
                                <i className="bi bi-clock"></i> {selectedTask.timeEstimate || "1 Hour"}
                            </h1>
                            <h1 className=''>Priority: <span className={`${selectedTask.priority === Priority.HIGH ? 'text-red-600' : selectedTask.priority === Priority.MEDIUM ? 'text-yellow-600' : 'text-green-600'}`}>{selectedTask.priority}</span></h1>
                        </div>
                        <h1 className='px-3.5 w-4/5 font-medium text-xl'>Description</h1>
                        <p className='px-3.5 py-3 text-sm'>{selectedTask.description || "No description provided."}</p>
                        <h1 className='px-3.5 py-2 w-4/5 font-medium text-xl'>Acceptance Criteria</h1>
                        {(selectedTask.acceptanceCriteria && selectedTask.acceptanceCriteria.length > 0 &&
                            selectedTask.acceptanceCriteria.some(criteria => criteria && criteria.trim() !== ''))
                            ? selectedTask.acceptanceCriteria.map((criteria, index) => (
                                criteria && criteria.trim() !== '' && (
                                    <div key={index} className='px-3 py-4 border-b border-gray-300 flex items-center'>
                                        <div className="rounded-xl">{index + 1}</div>
                                        <h1 className="ml-3.5">{criteria}</h1>
                                    </div>
                                )
                            ))
                            : <p className={`px-3.5 py-2 text-md ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-400'}`}>No acceptance criteria given.</p>
                        }
                        <h1 className='px-3.5 py-4 w-4/5 font-medium text-xl'>Assignment Details</h1>
                        <div>
                            <AssignmentDetails appMode={appMode} task={selectedTask} type="assignee" />
                            {selectedTask.assignedBy && <AssignmentDetails appMode={appMode} task={selectedTask} type="assigner" />}
                            <div className="px-3.5 py-2 flex justify-between items-center">
                                <h1 className={appMode === AppMode.LIGHT ? 'text-[#8E92BC]' : 'text-gray-400'}>Created At</h1>
                                <h1 className={appMode === AppMode.LIGHT ? 'text-[#141522]' : 'text-white'}>
                                    {selectedTask.createdAt
                                        ? typeof selectedTask.createdAt === 'string'
                                            ? new Date(selectedTask.createdAt).toLocaleString()
                                            : selectedTask.createdAt.toDate().toLocaleString()
                                        : "Unknown"
                                    }
                                </h1>
                            </div>
                        </div>
                        <h1 className='px-3.5 py-5 w-4/5 font-medium text-xl'>Upload Images/Files</h1>
                        <div className="px-3.5">
                            <TaskImageUpload appMode={appMode} onImageUpload={(imageUrl) => { }} />
                        </div>
                    </>
                    : <div className="flex flex-col items-center justify-center h-[calc(100%-80px)]">
                        <div className={`w-20 h-20 rounded-full flex items-center justify-center ${appMode === AppMode.LIGHT ? 'bg-gray-100' : 'bg-gray-800'} mb-4`}>
                            <i className={`bi bi-card-list text-4xl ${appMode === AppMode.LIGHT ? 'text-gray-400' : 'text-gray-500'}`}></i>
                        </div>
                        <h3 className="text-lg font-medium mb-2">No Task Selected</h3>
                        <p className={`text-sm text-center ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-400'} max-w-xs`}>Select a task from one of your boards to view its details and make updates.</p>
                    </div>
                }
            </div>
            {showCreateTeamModal && <CreateTeamModal appMode={appMode} onClose={() => setShowCreateTeamModal(false)} onTeamCreated={(team) => { setCurrentTeam(team); fetchTeamMembers(team.id); }} />}
        </div>
    );
}

export const useTasks = (status, teamId) => {
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!teamId) {
            setTasks([]);
            setLoading(false);
            return;
        }
        const isTeamTask = !teamId.startsWith('personal_');
        if (isTeamTask) {
            const tasksRef = ref(realtime, `teams/${teamId}/tasks`);
            const unsubscribe = onValue(tasksRef, (snapshot) => {
                const tasksData = snapshot.val() || {};
                const tasksList = Object.entries(tasksData).map(([id, data]) => ({
                    id,
                    ...data
                })).filter(task =>
                    status ? task.status === status : true
                );
                setTasks(tasksList);
                setLoading(false);
            });
            return () => unsubscribe();
        } else {
            let tasksQuery;
            if (status) {
                tasksQuery = query(
                    collection(firestore, 'tasks'),
                    where("status", "==", status),
                    where("teamId", "==", teamId)
                );
            } else {
                tasksQuery = query(
                    collection(firestore, 'tasks'),
                    where("teamId", "==", teamId)
                );
            }

            const unsubscribe = onSnapshot(tasksQuery, (snapshot) => {
                const tasksList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setTasks(tasksList);
                setLoading(false);
            }, (error) => {
                setLoading(false);
            });
            return () => unsubscribe();
        }
    }, [status, teamId]);
    return { tasks, loading };
};

export const addTask = async (taskData) => {
    try {
        const isTeamTask = !taskData.teamId.startsWith('personal_');
        if (isTeamTask) {
            const tasksRef = ref(realtime, `teams/${taskData.teamId}/tasks`);
            const newTaskRef = push(tasksRef);
            const taskToSave = {
                title: taskData.title,
                status: TaskStatus.TODO,
                assignedBy: auth.currentUser.uid,
                deadline: taskData.deadline || "",
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                description: taskData.description || "",
                priority: taskData.priority || Priority.MEDIUM,
                timeEstimate: taskData.timeEstimate || "1 Hour",
                acceptanceCriteria: taskData.acceptanceCriteria || [],
                assignedTo: taskData.assignedTo || auth.currentUser.uid,
            };
            await set(newTaskRef, taskToSave);
            await addDoc(collection(firestore, 'activityLogs'), {
                taskId: newTaskRef.key,
                userId: auth.currentUser.uid,
                action: 'created',
                timestamp: new Date(),
                teamId: taskData.teamId
            });
            return { success: true, id: newTaskRef.key };
        } else {
            const docRef = await addDoc(collection(firestore, 'tasks'), {
                title: taskData.title,
                status: TaskStatus.TODO,
                teamId: taskData.teamId,
                assignedBy: auth.currentUser.uid,
                deadline: taskData.deadline || "",
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                description: taskData.description || "",
                priority: taskData.priority || Priority.MEDIUM,
                timeEstimate: taskData.timeEstimate || "1 Hour",
                acceptanceCriteria: taskData.acceptanceCriteria || [],
                assignedTo: taskData.assignedTo || auth.currentUser.uid,
            });

            await addDoc(collection(firestore, 'activityLogs'), {
                taskId: docRef.id,
                userId: auth.currentUser.uid,
                action: 'created',
                timestamp: new Date(),
                teamId: taskData.teamId
            });
            return { success: true, id: docRef.id };
        }
    } catch (error) {
        return { success: false, error };
    }
};

export const updateTaskStatus = async (taskId, newStatus, teamId) => {
    try {
        const isTeamTask = teamId && !teamId.startsWith('personal_');
        if (isTeamTask) {
            const taskRef = ref(realtime, `teams/${teamId}/tasks/${taskId}`);
            const snapshot = await get(taskRef);
            if (!snapshot.exists()) {
                throw new Error("Task not found");
            }

            const taskData = snapshot.val();
            await update(taskRef, {
                status: newStatus,
                updatedAt: new Date().toISOString()
            });

            await addDoc(collection(firestore, 'activityLogs'), {
                taskId: taskId,
                timestamp: new Date(),
                teamId: teamId,
                userId: auth.currentUser.uid,
                action: `moved from ${taskData.status} to ${newStatus}`,
            });

            return { success: true };
        } else {
            const taskRef = doc(firestore, 'tasks', taskId);
            const taskDoc = await getDoc(taskRef);
            if (!taskDoc.exists()) {
                throw new Error("Task not found");
            }

            const taskData = taskDoc.data();
            await updateDoc(taskRef, {
                status: newStatus,
                updatedAt: new Date()
            });

            await addDoc(collection(firestore, 'activityLogs'), {
                taskId: taskId,
                timestamp: new Date(),
                teamId: taskData.teamId,
                userId: auth.currentUser.uid,
                action: `moved from ${taskData.status} to ${newStatus}`,
            });
            return { success: true };
        }
    } catch (error) {
        return { success: false, error };
    }
};

export const updateTaskDetails = async (taskId, updates, teamId) => {
    try {
        const isTeamTask = !teamId.startsWith('personal_');
        if (isTeamTask) {
            const taskRef = ref(realtime, `teams/${teamId}/tasks/${taskId}`);
            const snapshot = await get(taskRef);
            if (!snapshot.exists()) {
                throw new Error("Task not found");
            }

            await update(taskRef, {
                ...updates,
                updatedAt: new Date().toISOString(),
            });

            await addDoc(collection(firestore, 'activityLogs'), {
                taskId: taskId,
                userId: auth.currentUser.uid,
                action: 'updated details',
                timestamp: new Date(),
                teamId: teamId,
            });
            return { success: true };
        } else {
            const taskRef = doc(firestore, 'tasks', taskId);
            const taskDoc = await getDoc(taskRef);
            if (!taskDoc.exists()) {
                throw new Error("Task not found");
            }

            await updateDoc(taskRef, {
                ...updates,
                updatedAt: new Date(),
            });

            await addDoc(collection(firestore, 'activityLogs'), {
                taskId: taskId,
                userId: auth.currentUser.uid,
                action: 'updated details',
                timestamp: new Date(),
                teamId: taskDoc.data().teamId
            });
            return { success: true };
        }
    } catch (error) {
        return { success: false, error };
    }
};

export const deleteTask = async (taskId, teamId) => {
    try {
        const isTeamTask = !teamId.startsWith('personal_');
        if (isTeamTask) {
            const taskRef = ref(realtime, `teams/${teamId}/tasks/${taskId}`);
            const snapshot = await get(taskRef);
            if (!snapshot.exists()) {
                throw new Error("Task not found");
            }

            await remove(taskRef);
            await addDoc(collection(firestore, 'activityLogs'), {
                taskId: taskId,
                userId: auth.currentUser.uid,
                action: 'deleted',
                timestamp: new Date(),
                teamId: teamId,
            });
            return { success: true };
        } else {
            const taskRef = doc(firestore, 'tasks', taskId);
            const taskDoc = await getDoc(taskRef);
            if (!taskDoc.exists()) {
                throw new Error("Task not found");
            }

            const teamId = taskDoc.data().teamId;
            await deleteDoc(taskRef);
            await addDoc(collection(firestore, 'activityLogs'), {
                taskId: taskId,
                userId: auth.currentUser.uid,
                action: 'deleted',
                timestamp: new Date(),
                teamId: teamId
            });
            return { success: true };
        }
    } catch (error) {
        return { success: false, error };
    }
};